// ── BODOVÁNÍ ──────────────────────────────────────────────────────────────────
// 10 = přesný výsledek
//  6 = správný vítěz + správný rozdíl gólů NEBO správný vítěz + stejný počet gólů
//  4 = správný vítěz
//  2 = správný počet gólů NEBO stejný rozdíl
//  0 = nic
// Každý tip musí být unikátní v rámci zápasu. Remízy nejsou povoleny.

export function calcPts(tip, result) {
  if (!tip || !result) return null;
  const [th, ta, rh, ra] = [+tip.home, +tip.away, +result.home, +result.away];
  if ([th, ta, rh, ra].some(isNaN)) return null;
  const td = th - ta, rd = rh - ra;
  const tw = td > 0 ? 'h' : 'a', rw = rd > 0 ? 'h' : 'a';
  if (th === rh && ta === ra) return 10;
  if (tw === rw && (td === rd || (th + ta) === (rh + ra))) return 6;
  if (tw === rw) return 4;
  if ((th + ta) === (rh + ra) || Math.abs(td) === Math.abs(rd)) return 2;
  return 0;
}

export function ptLabel(p) {
  if (p === 10) return { t: 'Přesný tip!',     c: '#FFD60A', e: '🎯' };
  if (p === 6)  return { t: 'Vítěz + rozdíl',  c: '#34D399', e: '🥈' };
  if (p === 4)  return { t: 'Správný vítěz',   c: '#60A5FA', e: '✅' };
  if (p === 2)  return { t: 'Počet/rozdíl',    c: '#FB923C', e: '🟡' };
  return              { t: 'Žádné body',        c: '#F87171', e: '❌' };
}

// ── BANKA PIVO ────────────────────────────────────────────────────────────────
// Každé kolo:
// - bank = počet tipérů + carry z minula
// - Pokud padne přesný tip (10b): vítěz bere celý bank, mini se nevyplácí
// - Pokud nepade přesný tip: hráči s nejvyšším skóre (6>4>2) dostanou po 2 plzničkách
//   jako "mini výhra", ale jen pokud bank >= počet mini vítězů * 2
// - Zbytek se převalí do dalšího kola
// Každé kolo vrací:
//   matchId, bank, scored, winner (jackpot), miniWinners[], miniPayout, carry

export function bankHistory(matches, tips, members) {
  let carry = 0;
  const hist = [];
  const sorted = [...matches].sort((a, b) =>
    new Date(a.match_date) - new Date(b.match_date)
  );
  for (const m of sorted) {
    if (m.status !== 'finished' || m.result_home == null) continue;
    const result = { home: String(m.result_home), away: String(m.result_away) };
    const mt = tips.filter(t => t.match_id === m.id);
    const tippers = mt.filter(t => t.home != null && t.away != null);
    const bank = tippers.length + carry;
    const scored = tippers.map(t => ({
      name: t.member_name,
      p: calcPts({ home: String(t.home), away: String(t.away) }, result)
    }));

    // Jackpot: přesný tip
    const exactWinners = scored.filter(x => x.p === 10);
    const jackpotWinner = exactWinners.length === 1 ? exactWinners[0].name : null;

    let miniWinners = [];
    let miniPayout = 0;
    let newCarry = 0;

    if (jackpotWinner) {
      // Přesný tip padl → vítěz bere vše, mini se nevyplácí
      newCarry = 0;
    } else {
      // Hledáme mini výherce — nejvyšší skóre mimo 10b
      const maxP = scored.length ? Math.max(...scored.filter(x => x.p < 10).map(x => x.p)) : 0;
      if (maxP > 0) {
        const candidates = scored.filter(x => x.p === maxP);
        const needed = candidates.length * 2;
        if (bank >= needed) {
          miniWinners = candidates.map(x => x.name);
          miniPayout = needed;
        }
      }
      newCarry = bank - miniPayout;
    }

    carry = newCarry;
    hist.push({
      matchId: m.id,
      bank,
      scored,
      winner: jackpotWinner,       // vítěz jackpotu (přesný tip)
      miniWinners,                  // vítězové mini výhry (po 2 plzničkách)
      miniPayout,                   // celková výplata mini výhry
      carry: newCarry,
    });
  }
  return { hist, carry };
}

export function overallStats(matches, tips, members, hist, pivniOcas = null) {
  const s = {};
  members.forEach(m => { s[m.name] = { beers: 0, pts: 0, att: 0, exact: 0 }; });
  for (const m of matches) {
    if (m.status !== 'finished' || m.result_home == null) continue;
    const result = { home: String(m.result_home), away: String(m.result_away) };
    const mt = tips.filter(t => t.match_id === m.id && t.home != null);
    for (const t of mt) {
      if (!s[t.member_name]) s[t.member_name] = { beers: 0, pts: 0, att: 0, exact: 0 };
      const p = calcPts({ home: String(t.home), away: String(t.away) }, result);
      if (p === null) continue;
      s[t.member_name].att++;
      s[t.member_name].pts += p;
      if (p === 10) s[t.member_name].exact++;
    }
  }
  for (const r of hist) {
    // Jackpot výhra
    if (r.winner && s[r.winner]) s[r.winner].beers += r.bank;
    // Mini výhra (každý mini vítěz dostane 2 plzničky)
    r.miniWinners.forEach(name => {
      if (s[name]) s[name].beers += 2;
    });
  }
  if (pivniOcas) {
    const { winners, baseShare, remainder, tieBreak } = pivniOcas;
    winners.forEach((w, i) => {
      if (s[w.name]) s[w.name].beers += baseShare + (i < remainder ? 1 : 0);
    });
  }
  return s;
}

export function settlement(members, hist) {
  const net = {};
  members.forEach(m => { net[m.name] = 0; });
  for (const r of hist) {
    // Každý tipper vsadil 1 plzničku do tohoto kola
    r.scored.forEach(({ name }) => {
      if (!net[name]) net[name] = 0;
      net[name]--;
    });
    // Jackpot vítěz získá celý bank
    if (r.winner) {
      if (!net[r.winner]) net[r.winner] = 0;
      net[r.winner] += r.bank;
    }
    // Mini výherce dostane 2 plzničky
    r.miniWinners.forEach(name => {
      if (!net[name]) net[name] = 0;
      net[name] += 2;
    });
  }
  const cred = members.filter(m => net[m.name] > 0).map(m => ({ n: m.name, v: net[m.name] }));
  const debt = members.filter(m => net[m.name] < 0).map(m => ({ n: m.name, v: -net[m.name] }));
  const tx = [];
  let ci = 0, di = 0;
  while (ci < cred.length && di < debt.length) {
    const g = Math.min(cred[ci].v, debt[di].v);
    tx.push({ from: debt[di].n, to: cred[ci].n, b: g });
    cred[ci].v -= g; debt[di].v -= g;
    if (!cred[ci].v) ci++;
    if (!debt[di].v) di++;
  }
  return { net, tx };
}

// ── WA MESSAGE ────────────────────────────────────────────────────────────────
export function buildWA(match, tips, members, hist, comment, appName) {
  const rd = hist.find(h => h.matchId === match.id);
  const mt = tips.filter(t => t.match_id === match.id);
  let msg = `🏒 *${appName || 'Tipovačka o Plzničku'}*\n`;
  msg += `🇨🇿 Česko *${match.result_home}:${match.result_away}* ${match.opponent}\n`;
  msg += `📅 ${match.match_date} · ${match.phase}\n\n`;
  msg += `📊 *Výsledky tipů:*\n`;
  const rows = mt
    .map(t => {
      if (t.home == null) return null;
      const p = calcPts({ home: String(t.home), away: String(t.away) }, { home: String(match.result_home), away: String(match.result_away) });
      return { name: t.member_name, t, p, pl: ptLabel(p) };
    })
    .filter(Boolean)
    .sort((a, b) => b.p - a.p);

  rows.forEach(r => {
    const isMini = rd?.miniWinners?.includes(r.name);
    const isJackpot = r.name === rd?.winner;
    let prefix = r.pl.e;
    if (isJackpot) prefix = '🎯🏆';
    else if (isMini) prefix = '🍺';
    msg += `${prefix} *${r.name}*: ${r.t.home}:${r.t.away} → ${r.p} bodů${isMini ? ' _(mini výhra!)_' : ''}${isJackpot ? ' _(JACKPOT!)_' : ''}\n`;
  });

  msg += '\n';
  if (rd?.winner) {
    msg += `🎯🏆 *JACKPOT! ${rd.winner} bere ${rd.bank} 🍺!*\n`;
  } else if (rd) {
    msg += `❌ Hlavní bank nepadl — jackpot ${rd.carry + rd.miniPayout > 0 ? `přechází dál` : 'se převaluje'}\n`;
    if (rd.miniWinners.length > 0) {
      msg += `🍺 *Mini výhra:* ${rd.miniWinners.join(', ')} — každý po 2 🍺\n`;
    } else {
      msg += `😬 Mini výhra nevyplacena (málo piv v banku)\n`;
    }
  }
  if (comment) msg += `\n💬 ${comment}\n`;
  msg += `\n🍺 _${appName || 'Tipovačka o Plzničku'}_`;
  return msg;
}

// ── COLORS ────────────────────────────────────────────────────────────────────
export const MEMBER_COLORS = ['#EF4444','#3B82F6','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316'];
export const memberColor = name => MEMBER_COLORS[(name || '').charCodeAt(0) % MEMBER_COLORS.length];
